import React from 'react';
import styled from 'styled-components';
import { useIntl } from 'react-intl';
import { animated } from '@react-spring/web';
import Button from 'ui/Button/Button';
import { ButtonTypeEnum } from 'ui/Button/Button';
import IconButtonSizeEnum from '@inter-ikea-kompis/component-icon-button/lib/enums/IconButtonSizeEnum';
import Title from 'ui/Typography/Title';
import { ThemeFontStyleTypeEnum } from '@inter-ikea-kompis/enums/lib';
import { useSelected } from 'contexts/selected';
import SelectedMenuContent from './SelectedMenuContent';
import { SelectedMenuProductList } from 'rangeConfig/@types';
import MobileDrawer from 'components/MobileDrawer/MobileDrawer';
import ProductList from 'selectedMenu/ProductList';
import { useCore } from 'contexts/core';
import { getMenuTitle } from 'components/ProductSidebar/menus/utils/utils';
import CrossIcon from '@ingka/ssr-icon/paths/cross';
import { useLocation } from 'react-router-dom';
import { AppRoutes } from 'routes';

const MenuContainer = styled(animated.div)`
  position: fixed;
  transition: transform 0.1s ease-out;
  width: 100%;
  background: white;
  display: grid;
  grid-template-rows: 53px auto;
  z-index: 100;
`;

const OutletHeader = styled.div`
  display: flex;
  background: ${({ theme }) => theme.colors.grey[100]};
  height: 100%;
  width: 100%;
  align-items: center;
  justify-content: space-between;
  padding-left: ${({ theme }) => theme.space.md};
  padding-right: ${({ theme }) => theme.space.xs};
`;

const ContentWrapper = styled.div`
  display: flex;
  flex-direction: column;
  height: 100%;
`;

type Props = {
  animationProps: any;
  height: number;
};

const AdditionalContent = () => {
  const coreApi = useCore();
  const { formatMessage } = useIntl();
  const { additionalContent, toggleAdditionalContent } = useSelected();
  // If current page is summary, we should hide the mobile drawer as that causes the summary page to not be usable on mobile devices
  const currentPageIsSummary = useLocation().pathname === AppRoutes.SUMMARY;

  const title = getMenuTitle(
    additionalContent?.title,
    additionalContent?.titleValue,
    formatMessage,
    coreApi
  );

  return (
    <MobileDrawer
      open={additionalContent !== undefined && !currentPageIsSummary}
      title={title}
      onDismiss={() => toggleAdditionalContent(undefined)}
    >
      {additionalContent && (
        <ProductList
          alternativeProductList={additionalContent.alternativeSelectedProductList}
          content={additionalContent}
        />
      )}
    </MobileDrawer>
  );
};

const SelectedMenu = ({ animationProps, height }: Props) => {
  const { formatMessage } = useIntl();
  const { selectedMenu, unselect } = useSelected();
  const coreApi = useCore();

  const title = getMenuTitle(selectedMenu?.menuTitle, undefined, formatMessage, coreApi);
  const menuContent = selectedMenu?.content;

  return (
    <MenuContainer
      style={{
        transform: animationProps?.menuHeight.to(
          (menuHeight: number) => `translateY(-${menuHeight}px)`
        ),
        height: `${height}px`,
        bottom: `${-height}px`
      }}
    >
      <OutletHeader>
        <Title type={ThemeFontStyleTypeEnum.headingSmall} style={{ display: 'flex' }}>
          {title}
        </Title>
        <Button
          iconOnly
          size={IconButtonSizeEnum.small}
          type={ButtonTypeEnum.tertiary}
          onClick={unselect}
          ssrIcon={CrossIcon}
          dataTestId="selected-menu-close-button"
          aria-label={formatMessage({ id: 'storageone.closeButton' })}
        />
      </OutletHeader>
      <ContentWrapper>
        <SelectedMenuContent
          content={menuContent?.map(content => content as SelectedMenuProductList)}
        />
      </ContentWrapper>
      <AdditionalContent />
    </MenuContainer>
  );
};

export default SelectedMenu;
